import Ten99RouteConstants from 'Agent/1099s/Ten99RouteConstants';
import CommissionsRouteConstants from 'Agent/Commissions/CommissionsRouteConstants';
import {push} from 'connected-react-router';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {fromResult, Loaded, Loading, ServerError} from 'Core/models/Async';
import {ValidPassword} from 'Core/models/Password';
import {isRight} from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {all, call, put, select, takeLatest, takeEvery} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import {AgentNumberStatusId} from 'Shared/constants/AgentNumberStatus';
import {refreshToken} from 'Shared/utilities/Auth';
import {getUserClaims} from 'Shared/utilities/UserProfileUtilities';
import userApi from 'Users/api/userApi';
import * as E from 'fp-ts/lib/Either';
import {
  changePassword,
  changeUpdatePasswordError,
  setSelectedAgency,
  setUserClaims,
  updatePasswordByCurrent,
  updateCurrentPassword,
  updatePassword,
  UserActionTypes,
  resetTemporaryPassword,
} from 'Users/ducks/userProfile';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {createSnackbar} from 'Core/ducks/snackbar';
import {UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import {getIsNewYorkFromAgency} from 'Shared/utilities/isNewYorkUtility';
import {eq} from 'Shared/utilities/equals';
import {findFirst, head} from 'fp-ts/lib/Array';
import { getAxiosConfig, getUrlFromUri } from 'Shared/utilities/fetchHelpers';
import axios from 'Shared/utilities/axiosInstance';
import { PasswordUpdateRequestDto } from 'Users/api';
import {SailpointApplicationContainer} from '../../Shared/constants/UserRecovery';

const isCommissionsRoute = (pathname: string) => pathname.includes(CommissionsRouteConstants.COMMISSIONS);
const is1099sRoute = (pathname: string) => pathname.includes(Ten99RouteConstants.TEN99s);
const isCurrentlyAtPageOnlyAccessibleToActiveAgencies = (pathname: string) =>
  isCommissionsRoute(pathname) || is1099sRoute(pathname);

function* handleTaxIdSwitchSaga() {
  yield takeLatest(UserActionTypes.SET_SELECTED_AGENCY, function*(action: ReturnType<typeof setSelectedAgency>) {
    const {selectedAgency} = action.payload;
    const location: Location = yield select((state: ReducerState) => state.router.location);
    const hasAllAgentNumbersTerminated = pipe(
      selectedAgency,
      O.map(agency => agency.overallAgentNumberStatus.id === AgentNumberStatusId.Terminated),
      O.getOrElse(() => false)
    );

    if (!isCurrentlyAtPageOnlyAccessibleToActiveAgencies(location.pathname) && hasAllAgentNumbersTerminated) {
      yield put(push(CoreRouteConstants.DASHBOARD));
    }
  });
}

function* updatePasswordByUserSaga() {
  yield takeLatest(UserActionTypes.UPDATE_PASSWORD, function*(action: ReturnType<typeof updatePassword>) {
    const selectedAgency: O.Option<AgentAgencyClaim> = yield select(
      (state: ReducerState) => state.user.profile.selectedAgency
    );

    if (!O.isNone(selectedAgency)) {
      yield put(changePassword(new Loading()));

      const result: any = yield call(
        userApi.updatePasswordByUser,
        action.payload.userId,
        action.payload.updatePasswordRequest,
        selectedAgency.value.encryptedIdentifier
      );

      if (isRight(result)) {
        yield put(changeUpdatePasswordError());
        yield put(createSnackbar('Password updated successfully.'));

        if (action.payload.onReset) {
          action.payload.onReset();
          sessionStorage.setItem('wasPasswordUpdated', 'true');
          yield call(refreshToken);

          const newClaims = pipe(getUserClaims());

          if (O.isSome(newClaims)) {
            yield put(setUserClaims(fromResult(newClaims.value)));
          }
        }
      } else {
        yield put(
          changeUpdatePasswordError(
            result.left instanceof ServerError
              ? result.left.errorDetails
              : 'Something went wrong. Please try again.'
          )
        );
      }
      yield put(changePassword(new Loaded(ValidPassword.create('', ''))));
      yield put(updateCurrentPassword(''));
    }
  });
}

export function* updatePasswordWithOldPassword() {
  yield takeLatest(UserActionTypes.UPDATE_PASSWORD_CURRENT, function*(
    action: ReturnType<typeof updatePasswordByCurrent>
  ) {
    const selectedAgency: O.Option<AgentAgencyClaim> = yield select(
      (state: ReducerState) => state.user.profile.selectedAgency
    );

    if (!O.isNone(selectedAgency)) {
      yield put(changePassword(new Loading()));

      const updatePassword = (passwordDetails: PasswordUpdateRequestDto) => {
        const axiosConfig = getAxiosConfig();
        const ADDRESS_ROOT_URL: string = `api/user/v1/portal/password/updatePassword/${SailpointApplicationContainer.BR}`;
        return axios
          .put(getUrlFromUri(`${ADDRESS_ROOT_URL}`), passwordDetails, {
            ...axiosConfig,
            headers: {
              ...axiosConfig.headers,
            },
          })
          .then((resp)=> resp.status===200 ? E.right(resp.data): 
          E.left(resp.data.msgStatus.extendedStatusList.extendedStatus[0].extendedStatusReasonCd));
      };

      const result: any = yield updatePassword({oldPassword: action.payload.currentPassword, newPassword: action.payload.newPassword})

      if (isRight(result)) {
        yield put(changeUpdatePasswordError());
        yield put(createSnackbar('Password updated successfully.'));

        if (action.payload.onReset) {
          action.payload.onReset();

          yield call(refreshToken);

          const newClaims = pipe(getUserClaims());

          if (O.isSome(newClaims)) {
            yield put(setUserClaims(fromResult(newClaims.value)));
          }
        }
      } else {
        yield put(
          changeUpdatePasswordError(
            result.left instanceof ServerError
              ? result.left.errorDetails
              : 'Something went wrong. Please try again.'
          )
        );
      }

      yield put(changePassword(new Loaded(ValidPassword.create('', ''))));
      yield put(updateCurrentPassword(''));
    }
  });
}

export function* ResetPasswordWithOldPassword() {
  yield takeLatest(UserActionTypes.RESET_TEMPORARY_PASSWORD, function*(
    action: ReturnType<typeof resetTemporaryPassword>
  ) {
    const selectedAgency: O.Option<AgentAgencyClaim> = yield select(
      (state: ReducerState) => state.user.profile.selectedAgency
    );

    if (!O.isNone(selectedAgency)) {
      yield put(changePassword(new Loading()));

      const result: any = yield call(
        userApi.updatePasswordWithOldPassword,
        action.payload.userId,
        {oldPassword: action.payload.currentPassword, newPassword: action.payload.newPassword},
        selectedAgency.value.encryptedIdentifier
      );

      if (isRight(result)) {
        yield put(changeUpdatePasswordError());
        yield put(createSnackbar('Password updated successfully.'));

        if (action.payload.onReset) {
          action.payload.onReset();
          sessionStorage.setItem('wasPasswordUpdated', 'true');
          yield call(refreshToken);

          const newClaims = pipe(getUserClaims());

          if (O.isSome(newClaims)) {
            yield put(setUserClaims(fromResult(newClaims.value)));
          }
        }
        yield put(changePassword(new Loaded(ValidPassword.create('', ''))));
        yield put(updateCurrentPassword(''));
      } else {
        if (result.left instanceof ServerError) {
          yield put(
            updatePassword(action.payload.userId, action.payload.updatePasswordRequest, action.payload.onReset)
          );
        } else {
          yield put(
            changeUpdatePasswordError(
              result.left instanceof ServerError ? result.left.errorDetails : 'Something went wrong. Please try again.'
            )
          );
          yield put(changePassword(new Loaded(ValidPassword.create('', ''))));
          yield put(updateCurrentPassword(''));
        }
      }
    }
  });
}

export function* refreshUserTokenSaga() {
  yield call(refreshToken);

  const newClaims = pipe(getUserClaims());
  if (O.isSome(newClaims)) {
    const agentClaims = pipe(
      newClaims.value.userTypes,
      findFirst(eq('$type', UserTypeClaimKey.AgentSite)),
      O.chain(
        UserTypeClaims.match({
          [UserTypeClaimKey.AgentSite]: agentClaims => {
            const impersonatedAgentClaims = O.fromNullable(agentClaims.impersonatedAgentClaim);
            return O.isSome(impersonatedAgentClaims) ? impersonatedAgentClaims : O.some(agentClaims);
          },
          default: () => O.none,
        })
      )
    );

    if (O.isSome(agentClaims)) {
      const defaultAgency = pipe(
        agentClaims.value.agencyAssociations.filter(aga => aga.isDefault),
        head
      );
      const isNewYork = getIsNewYorkFromAgency(defaultAgency);
      yield put(setSelectedAgency(defaultAgency, isNewYork));
    }
  }
}

export function* refreshUserToken() {
  yield takeEvery(UserActionTypes.REFRESH_USER_TOKEN, refreshUserTokenSaga);
}
export default function*() {
  yield all([
    handleTaxIdSwitchSaga(),
    updatePasswordByUserSaga(),
    refreshUserToken(),
    updatePasswordWithOldPassword(),
    ResetPasswordWithOldPassword(),
  ]);
}
